import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Button,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import BlockIcon from '@mui/icons-material/Block';
import {
  caregiverVerificationService,
  QueueCaregiver,
  VerificationDecisionRow,
} from '../services/caregiverVerificationService';

// FEAT-039 — Admin Caregiver Verification side drawer.
// Opens on a row click in the queue. Renders the caregiver context (name,
// email, state, license number) and the credential documents the admin
// must review. The admin then triggers Verify (optional reason) or Reject
// (reason REQUIRED). Both decisions audit-log via the service.

export interface VerificationReviewSidebarProps {
  caregiver: QueueCaregiver | null;
  open: boolean;
  onClose: () => void;
  onDecided: (caregiverId: string, decision: VerificationDecisionRow) => void;
}

const VerificationReviewSidebar: React.FC<VerificationReviewSidebarProps> = ({
  caregiver,
  open,
  onClose,
  onDecided,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState<'verify' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
      setSubmitting(null);
    }
  }, [open, caregiver?.id]);

  if (!caregiver) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: 480, p: 3 }}>
          <Typography>No caregiver selected.</Typography>
        </Box>
      </Drawer>
    );
  }

  const handleVerify = async () => {
    setSubmitting('verify');
    setError(null);
    try {
      const result = await caregiverVerificationService.verify(
        caregiver.id,
        reason.trim() || undefined,
      );
      onDecided(caregiver.id, result.decision);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(null);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      setError('A reason is required to reject a verification.');
      return;
    }
    setSubmitting('reject');
    setError(null);
    try {
      const result = await caregiverVerificationService.reject(
        caregiver.id,
        reason.trim(),
      );
      onDecided(caregiver.id, result.decision);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed');
    } finally {
      setSubmitting(null);
    }
  };

  const fullName = [caregiver.firstName, caregiver.lastName]
    .filter(Boolean)
    .join(' ') || 'Caregiver';

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{ width: 520, p: 3 }}
        role="region"
        aria-label="Caregiver verification review"
        data-testid="verification-review-sidebar"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Verify caregiver</Typography>
          <IconButton onClick={onClose} aria-label="Close verification review">
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="subtitle1">{fullName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {caregiver.email}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            {caregiver.stateCode && (
              <Chip label={`State: ${caregiver.stateCode}`} size="small" />
            )}
            {caregiver.stateLicenseNumber && (
              <Chip
                label={`License: ${caregiver.stateLicenseNumber}`}
                size="small"
                color="primary"
              />
            )}
          </Stack>

          <Divider />

          <Typography variant="subtitle2">Uploaded credential documents</Typography>
          {caregiver.credentialDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No documents uploaded yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {caregiver.credentialDocuments.map((doc) => (
                <ListItem
                  key={doc.id}
                  data-testid={`verification-doc-${doc.id}`}
                  sx={{ pl: 0 }}
                >
                  <ListItemText
                    primary={`${doc.title} (${doc.docType})`}
                    secondary={`Uploaded ${new Date(doc.uploadedAt).toLocaleString()} · ${doc.status}`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {caregiver.latestDecision?.decision === 'REJECTED' && (
            <Alert severity="warning" data-testid="verification-prior-rejection">
              Previously rejected: {caregiver.latestDecision.reason ?? 'No reason recorded.'}
            </Alert>
          )}

          <Divider />

          <TextField
            label="Reason (required for rejection, optional for verification)"
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ 'data-testid': 'verification-reason-input', maxLength: 2000 }}
            fullWidth
          />

          {error && (
            <Alert severity="error" data-testid="verification-error">
              {error}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="success"
              startIcon={
                submitting === 'verify' ? <CircularProgress size={16} /> : <VerifiedIcon />
              }
              disabled={!!submitting}
              onClick={handleVerify}
              data-testid="verification-verify-button"
            >
              Verify
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={
                submitting === 'reject' ? <CircularProgress size={16} /> : <BlockIcon />
              }
              disabled={!!submitting}
              onClick={handleReject}
              data-testid="verification-reject-button"
            >
              Reject
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default VerificationReviewSidebar;
